import { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import { cx } from '@libs/classnames';
import { pluralize } from '@libs/i18n';
import { Badge } from '@ui/components/Badge';
import { MdText } from '@ui/components/MdText';

import { ClubInfo } from '@features/club/ui/ClubInfo';
import { ClubLogo } from '@features/club/ui/ClubLogo';
import { MemberClubDto } from '@features/shared/api/typings';
import { useSubheaderMediaQuery } from '@features/shared/hooks/useSubheaderMediaQuery';

import styles from './ClubSubHeader.module.css';

const MEMBERS_PLURAL = {
  one: '{number} участник',
  few: '{number} участника',
  many: '{number} участников',
  zero: '{number} участников',
};

const TOURNAMENTS_PLURAL = {
  one: '{number} турнир',
  few: '{number} турнира',
  many: '{number} турниров',
  zero: '{number} турниров',
};

type Props = {
  club?: MemberClubDto;
  actions?: ReactNode;
};
export function ClubSubHeader({ club, actions }: Props) {
  const { device } = useSubheaderMediaQuery();

  if (!club) return null;

  const sponsor = club.sponsors?.[0];

  const logo = (
    <Link to="/club/$clubId" params={{ clubId: club.clubId }}>
      <ClubLogo
        className={styles.clubLogo}
        logoUrl={club.logoUrl}
        sponsorLogoUrl={sponsor?.logoUri}
      />
    </Link>
  );

  return (
    <div className={cx(styles.subHeader, device)}>
      <div className={styles.title}>{club?.title}</div>
      <div className={styles.logoWithActions}>
        {logo}
        {actions}
      </div>
      <div className={styles.logo}>{logo}</div>
      <div className={styles.actions}>{actions}</div>
      <div className={styles.badges}>
        <ClubBadges club={club} />
      </div>
      <div className={styles.description}>
        <MdText value={club?.description} />
      </div>
      <div className={styles.info}>
        <ClubInfo club={club} />
      </div>
    </div>
  );
}

function ClubBadges({ club }: { club?: MemberClubDto }) {
  if (!club) return;

  return (
    <>
      <Badge size="m" flat>
        {pluralize(MEMBERS_PLURAL, club.membersCount)}
      </Badge>
      <Badge size="m" flat>
        {pluralize(TOURNAMENTS_PLURAL, club.finishedTournamentsCount)}
      </Badge>
    </>
  );
}
